import { useEffect, useRef, type PointerEvent, type RefObject } from "react";
import type { ScratchPadMode, ScratchPadTextItem } from "./types";

type Props = Readonly<{
  items: readonly ScratchPadTextItem[];
  mode: ScratchPadMode;
  activeId: string | null;
  containerRef: RefObject<HTMLDivElement>;
  onChangeText: (id: string, text: string) => void;
  onMoveText: (id: string, x: number, y: number) => void;
  onActivate: (id: string) => void;
  onRemove: (id: string) => void;
}>;

const clamp = (min: number, max: number, value: number) =>
  Math.max(min, Math.min(max, value));

export const ScratchPadTextLayer = ({
  items,
  mode,
  activeId,
  containerRef,
  onChangeText,
  onMoveText,
  onActivate,
  onRemove,
}: Props) => (
  <div
    className={[
      "absolute inset-0",
      mode === "text" ? "pointer-events-auto" : "pointer-events-none",
    ].join(" ")}
  >
    {items.map((item) => (
      <ScratchPadTextItemView
        key={item.id}
        item={item}
        mode={mode}
        active={activeId === item.id}
        containerRef={containerRef}
        onChangeText={onChangeText}
        onMoveText={onMoveText}
        onActivate={onActivate}
        onRemove={onRemove}
      />
    ))}
  </div>
);

type ItemProps = Readonly<{
  item: ScratchPadTextItem;
  mode: ScratchPadMode;
  active: boolean;
  containerRef: RefObject<HTMLDivElement>;
  onChangeText: (id: string, text: string) => void;
  onMoveText: (id: string, x: number, y: number) => void;
  onActivate: (id: string) => void;
  onRemove: (id: string) => void;
}>;

export const ScratchPadTextItemView = ({
  item,
  mode,
  active,
  containerRef,
  onChangeText,
  onMoveText,
  onActivate,
  onRemove,
}: ItemProps) => {
  const dragRef = useRef<{
    startX: number;
    startY: number;
    originX: number;
    originY: number;
    w: number;
    h: number;
    cw: number;
    ch: number;
    pointerId: number;
    dragging: boolean;
  } | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (active) inputRef.current?.focus();
  }, [active]);

  const handlePointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (mode !== "text") return;
    const container = containerRef.current;
    if (!container) return;
    const containerRect = container.getBoundingClientRect();
    const itemRect = e.currentTarget.getBoundingClientRect();
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      originX: itemRect.left - containerRect.left,
      originY: itemRect.top - containerRect.top,
      w: itemRect.width,
      h: itemRect.height,
      cw: containerRect.width,
      ch: containerRect.height,
      pointerId: e.pointerId,
      dragging: false,
    };
    onActivate(item.id);
  };

  const handlePointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    if (!drag.dragging) {
      if (Math.abs(dx) + Math.abs(dy) < 4) return;
      drag.dragging = true;
      e.currentTarget.setPointerCapture(drag.pointerId);
    }
    const nextX = clamp(0, drag.cw - drag.w, drag.originX + dx);
    const nextY = clamp(0, drag.ch - drag.h, drag.originY + dy);
    onMoveText(item.id, nextX / drag.cw, nextY / drag.ch);
  };

  const handlePointerUp = () => {
    dragRef.current = null;
  };

  return (
    <div
      className="absolute touch-none cursor-move"
      style={{
        left: `${item.x * 100}%`,
        top: `${item.y * 100}%`,
        width: `${item.w * 100}%`,
      }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      <div className="relative rounded-[18px] border border-slate-200 bg-transparent">
        <button
          type="button"
          onClick={() => onRemove(item.id)}
          className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-white border border-slate-200 text-slate-500 text-[14px] leading-none"
        >
          ×
        </button>
        <textarea
          ref={inputRef}
          value={item.text}
          onChange={(e) => onChangeText(item.id, e.target.value)}
          onFocus={() => onActivate(item.id)}
          placeholder="Type here..."
          className="h-full w-full resize-none bg-transparent px-4 py-3 text-[18px] leading-7 font-shantell font-semibold text-slate-600 placeholder:text-slate-400 outline-none"
          rows={3}
        />
      </div>
    </div>
  );
};
